import { Component, DestroyRef, HostListener, Inject, OnInit, Renderer2 } from '@angular/core';
import { MainMenuService } from './layout/main-menu.service';
import { developerMenuItems, mainMenuItems } from './main-menu-items';
import { fromEvent, Observable, take } from 'rxjs';
import { EdgeActions } from '@states/edge/edge.action-types';
import { select, Store } from '@ngrx/store';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import * as SharedActions from '@states/shared/shared.actions';
import { APP_INACTIVITY_THRESHOLD, APP_INACTIVITY_TIME } from '@consts/general.const';
import { UserSettings } from '@models/user-settings';
import * as UserSettingsSelectors from '@states/user-settings/user-settings.selectors';
import { MatDialog } from '@angular/material/dialog';
import { InactivityModalComponent } from './shared/modals/inactivity-modal/inactivity-modal.component';
import { SwUpdateNotificationService } from './shared/sw-update-notification.service';
import { DOCUMENT } from '@angular/common';
import { EdgeStatusService } from './edge/edge-status.service';
import { EdgeEffects } from '@effects/edge.effects';
import { IconsService } from './shared/icons.service';
import { UserSelectors } from '@states/user/user.selector-types';
import { UserActions } from '@states/user/user.action-types';
import { BrowserCompatibilityModalComponent } from '@modals/browser-compatibility-modal/browser-compatibility-modal.component';
import { SwPush } from '@angular/service-worker';
import { NotificationService } from './services/notification.service';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { ActiveOrganization } from '@models/organization.model';
import { withLatestFrom } from 'rxjs/operators';
import { GlobalKeyboardService } from './shared/global-keyboard.service';
import { LOG_CONTEXT } from './tokens/log-context.token';
import { ContextualLoggerService } from './services/contextual-logger.service';
import { routerSegments } from '@consts/routes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

declare const document: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  providers: [{ provide: LOG_CONTEXT, useValue: 'AppComponent' }, ContextualLoggerService],
})
export class AppComponent implements OnInit {
  isSearchOpen = false;

  public selectIsDeveloper$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDeveloper));
  public selectSelectedUserSettings$: Observable<UserSettings> = this.store$.pipe(
    select(UserSettingsSelectors.selectSelectedUserSettings),
  );

  readonly VAPID_PUBLIC_KEY =
    'BLNcbeY7KpjyGl8qxWclBBLjW7sOyOVwQqLbRftdOBkP43Brii7AfHfNghaCPdvMpCRO4Sp0zk8VbYds5H7jijY';

  public selectActiveOrganization$: Observable<ActiveOrganization> = this.store$.pipe(
    select(OrganizationSelectors.selectActiveOrganization),
  );

  onlineStatus: Observable<Event>;
  offlineStatus: Observable<Event>;
  public isExpanded: boolean = false;
  public autoLogoutCounter = 0; // in minutes
  public isAutoLogoutEnabled = false;
  private inactivityTime = 0;
  private inactivityTimerInterval = null;
  private autoLogoutOpened = false;
  private disableInactivity = false;

  showNotification = false;
  notificationMessage = [];

  constructor(
    private mainMenuService: MainMenuService,
    private iconsService: IconsService,
    private store$: Store,
    private renderer2: Renderer2,
    private dialog: MatDialog,
    private swUpdateNotificationService: SwUpdateNotificationService,
    private edgeStatusService: EdgeStatusService,
    private edgeEffects$: EdgeEffects,
    private swPush: SwPush,
    @Inject(DOCUMENT) private document: Document,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private globalKeyboardService: GlobalKeyboardService,
    private logger: ContextualLoggerService,
    private destroyRef: DestroyRef,
  ) {

    this.store$.dispatch(UserActions.SessionIdentity());

    this.store$
      .pipe(select(OrganizationSelectors.selectActiveOrganization), takeUntilDestroyed(this.destroyRef))
      .subscribe((activeOrganization) => {
        if (activeOrganization) {
          this.disableInactivity = activeOrganization?.disableInactivity ?? false;
        }
      });
  }

  title = 'Lumana';

  @HostListener('window:keydown')
  @HostListener('window:mousemove')
  @HostListener('window:mousedown')
  checkUserActivity() {
    if (this.inactivityTimerInterval) {
      clearInterval(this.inactivityTimerInterval);
      this.inactivityTime = 0;
    }
    this.inactivityTimerInterval = setInterval(() => {
      if (this.inactivityTime === APP_INACTIVITY_THRESHOLD - 1) {
        if (!this.disableInactivity) {
          this.store$.dispatch(SharedActions.startInactivityCountdown());
        }
      }
      if (this.inactivityTime >= APP_INACTIVITY_TIME) {
        if (!this.disableInactivity) {
          this.store$.dispatch(SharedActions.setIsInactive({ isInactive: true }));
        }
        clearInterval(this.inactivityTimerInterval);
        this.inactivityTime = 0;
      }
      /**
       * NOTE: need to check if counter Enabled
       */
      if (this.isAutoLogoutEnabled) {
        if (this.inactivityTime >= this.autoLogoutCounter * 60 - 30 && !this.autoLogoutOpened) {
          this.autoLogoutOpened = true;
          const inactivityModal = this.dialog.open(InactivityModalComponent, {
            width: '475px',
            panelClass: 'modal-no-padding',
            disableClose: true,
            data: {
              autoLogoutCounter: 30, //show on 30 sec before finish
            },
          });
          inactivityModal.afterClosed().subscribe((res) => {
            clearInterval(this.inactivityTimerInterval);
            this.inactivityTime = 0;
            this.autoLogoutOpened = false;
          });
        }
      }

      this.inactivityTime++;
      this.store$.dispatch(SharedActions.setInactivityTime({ inactivityTime: this.inactivityTime }));
    }, 1000);
  }

  public ngOnInit(): void {
    const url = new URL(window.location.href);
    const isEmbedded = url.searchParams.get('embedded');

    this.enforceClickjackingProtection(url, isEmbedded === 'true');

    if (!isEmbedded) {
      if (isNotChromeOrEdge()) {
        this.dialog.open(BrowserCompatibilityModalComponent, {
          width: '516px',
        });
        this.logger.log('The browser is not Chrome.');
      } else {
        this.logger.log('The browser is Chrome.');
      }
    }

    // this.globalKeyboardService.searchDialogOpened.subscribe((opened: boolean) => {
    //   this.isSearchOpen = opened;
    // });

    function isNotChromeOrEdge() {
      let userAgent = navigator.userAgent;
      let isChrome = /Chrome/.test(userAgent) && !/Edg|OPR|Brave/.test(userAgent);
      let isEdge = /Edg/.test(userAgent); // Microsoft Edge detection
      return !isChrome && !isEdge;
    }

    this.selectSelectedUserSettings$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.autoLogoutCounter = res?.autoLogout;
      this.isAutoLogoutEnabled = res?.autoLogoutEnabled;
    });

    this.checkUserActivity();
    this.onlineStatus = fromEvent(window, 'online');
    this.offlineStatus = fromEvent(window, 'offline');
    this.offlineStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.logger.log('[APP] Online');
    });
    this.onlineStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.store$
        .select(EdgeSelectors.selectAllEdges)
        .pipe(take(1), withLatestFrom(this.selectActiveOrganization$.pipe(take(1))))
        .subscribe(([edges, org]) => {
          this.logger.log('[APP] Online');
            if (org?.localLiveStream) {
              const edgeIds = edges.map((edge) => edge.edgeId);
              this.store$.dispatch(EdgeActions.ResetEdgesIsLocal({ request: { edgeIds } }));
              this.store$.dispatch(
                EdgeActions.GetEdgesLocalNetwork({
                  edgeIds,
                  localLiveStream: org?.localLiveStream,
                  webLocalLiveViewType: org?.webLocalLiveViewType,
                }),
              );
          }
        });
    });

    this.mainMenuService.config = {
      isInitiallyOpened: true,
      displayIconsOnCollapse: false,
      items: mainMenuItems,
      developerItems: developerMenuItems,
    };

    this.swUpdateNotificationService.currentNotification.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((message) => {
      this.notificationMessage = message;
      this.showNotification = !!message && !this.swUpdateNotificationService.isManuallyClosed;
    });

    this.edgeStatusService.subscribeToEdgeStatusSocket();
    this.edgeStatusService.subscribeToStabilityIssuesSocket();
    //
    // this.edgeEffects$.getEdgesInitialPulsationStatus$
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe(_ => {
    //     this.store$.select(EdgeSelectors.selectAllEdges)
    //       .pipe(takeUntilDestroyed(this.destroyRef), take(1), filter(edges => !!edges && edges.length > 0))
    //       .subscribe((edges) => {
    //         const subscription: Observable<PulsationModels.PulsationResponse>[] = [];
    //         edges.forEach((edge) => {
    //           const sub = this.edgeStatusService.getPulsationFromSinglestoreV2(edge.edgeId, undefined, true);
    //           subscription.push(sub);
    //         });
    //         forkJoin(subscription)
    //           .subscribe(res =>
    //             console.log('ALL REQUEST EDGES FINISHED'),
    //           );
    //       });
    //
    //   });

    // this.subscribeToNotifications();
  }


  public subscribeToNotifications(): void {
    this.swPush
      .requestSubscription({
        serverPublicKey: this.VAPID_PUBLIC_KEY,
      })
      .then((sub) => {
        this.logger.log(sub);
        this.notificationService.subscribeToWebPushNotifications(sub).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      })
      .catch((err) => this.logger.error('Could not subscribe to notifications', err));

    this.swPush.notificationClicks.subscribe((res) => {
      this.logger.log(res);
    });
  }

  private enforceClickjackingProtection(url: URL, isEmbeddedParam: boolean): void {
    if (window.self === window.top) {
      return;
    }

    const isGrantedAccessRoute = url.pathname.startsWith(`/${routerSegments.grantedAccess}`);
    if (isGrantedAccessRoute && isEmbeddedParam) {
      return;
    }

    this.logger.error('[SECURITY] App loaded in a disallowed iframe');

    // Cross-origin frames cannot navigate the top window (browser blocks it; see Chrome
    // "Unsafe attempt to initiate navigation"). Same-origin-only breakout is attempted;
    // otherwise we end the session so credentials are not usable inside a hostile embed.
    if (!this.isFramedCrossOrigin()) {
      try {
        window.top!.location.href = window.self.location.href;
        return;
      } catch (e) {
        this.logger.error('[SECURITY] Same-origin iframe breakout failed', e);
      }
    }

    this.store$.dispatch(
      AuthenticationActions.Logout({
        params: {
          msg:
            'For your security, this application cannot run inside an embedded frame. Open it in a full browser window.',
        },
      }),
    );
  }

  /** True when embedded in a frame with a different origin than the top window (or top is not readable). */
  private isFramedCrossOrigin(): boolean {
    try {
      if (window.self === window.top) {
        return false;
      }
      return window.top!.location.origin !== window.self.location.origin;
    } catch {
      return true;
    }
  }
}
